import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, ExternalLink, Search, Plus, MoreHorizontal,
  CheckCircle2, Ban, AlertCircle, Eye,
} from "lucide-react";
import { api } from "../../lib/auth";

function NewTenantDialog({ open, onClose, plans, onCreated }) {
  const [form, setForm] = useState({
    subdomain: "", company_name: "", admin_email: "", password: "",
    plan_id: "", billing_cycle: "monthly", activate: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (plans?.length && !form.plan_id) setForm((f) => ({ ...f, plan_id: plans[0].id }));
  }, [plans]); // eslint-disable-line

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be 8+ chars");
    setBusy(true);
    try {
      await api.post("/admin/tenants", form);
      toast.success("Tenant created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-black/8">
          <h2 className="font-display font-bold text-xl text-black">Create Tenant</h2>
          <p className="text-black/55 text-sm mt-0.5">Provision a new workspace on behalf of a customer.</p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field lbl="Company name" value={form.company_name} testid="tf-company"
              onChange={(v) => setForm({ ...form, company_name: v })} required />
            <Field lbl="Subdomain" value={form.subdomain} testid="tf-subdomain"
              onChange={(v) => setForm({ ...form, subdomain: v.toLowerCase() })} required hint="lowercase, no spaces" />
            <Field lbl="Admin email" type="email" value={form.admin_email} testid="tf-email"
              onChange={(v) => setForm({ ...form, admin_email: v })} required />
            <Field lbl="Password" type="password" value={form.password} testid="tf-password"
              onChange={(v) => setForm({ ...form, password: v })} required hint="min 8 chars" />
            <div>
              <label className="text-xs font-bold text-black/60 uppercase tracking-wider">Plan</label>
              <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                data-testid="tf-plan"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm bg-white">
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-black/60 uppercase tracking-wider">Billing</label>
              <select value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
                data-testid="tf-billing"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm bg-white">
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-black/70 pt-2 cursor-pointer">
            <input type="checkbox" checked={form.activate}
              onChange={(e) => setForm({ ...form, activate: e.target.checked })}
              data-testid="tf-activate"
              className="w-4 h-4 accent-[#1B4332]" />
            Activate immediately (skip payment flow)
          </label>
          <div className="pt-3 flex justify-end gap-2 border-t border-black/8">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-lg border border-black/15 text-sm font-semibold hover:bg-black/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="tf-submit"
              className="px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124] disabled:opacity-60">
              {busy ? "Creating..." : "Create tenant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ lbl, value, onChange, type = "text", hint, required, testid }) {
  return (
    <div>
      <label className="text-xs font-bold text-black/60 uppercase tracking-wider">{lbl}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
      {hint && <div className="text-[10px] text-black/45 mt-1">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const m = {
    active: "bg-[#E8F5E9] text-[#1FA868]",
    pending: "bg-[#FFF5D6] text-amber-700",
    rejected: "bg-rose-50 text-rose-600",
    suspended: "bg-slate-100 text-slate-700",
    cancelled: "bg-black/5 text-black/60",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${m[status] || "bg-black/5 text-black/60"}`}>{status}</span>;
}

export default function AdminTenants() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState(params.get("status_filter") || "all");
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/tenants", { params: { status_filter: f } });
      setTenants(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filter); setParams({ status_filter: filter }); }, [filter]);

  const quickStatus = async (t, status, reason = "") => {
    try {
      await api.post(`/admin/tenants/${t.id}/status`, { status, reason });
      toast.success(`Tenant ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = tenants.filter((t) =>
    !search ||
    t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.subdomain?.toLowerCase().includes(search.toLowerCase()) ||
    t.admin_email?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    all: tenants.length,
    pending: tenants.filter((t) => t.status === "pending").length,
    active: tenants.filter((t) => t.status === "active").length,
    suspended: tenants.filter((t) => t.status === "suspended").length,
    rejected: tenants.filter((t) => t.status === "rejected").length,
  };

  return (
    <div data-testid="admin-tenants-page" className="p-8 lg:p-10 max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-black tracking-tight">Tenants</h1>
          <p className="text-black/60 mt-1.5 text-sm">Approve, suspend, or manage tenant workspaces.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="tenants-new-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124]">
          <Plus size={14} /> New Tenant
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-black/10 px-3 py-2 flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="tenants-search" placeholder="Search by name, email, or subdomain"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { k: "all", lbl: "All" },
            { k: "pending", lbl: "Pending" },
            { k: "active", lbl: "Active" },
            { k: "suspended", lbl: "Suspended" },
            { k: "rejected", lbl: "Rejected" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                filter === f.k ? "bg-[#1B4332] text-white" : "bg-white border border-black/10 text-black/70 hover:border-black/25"
              }`}>
              {f.lbl}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${filter === f.k ? "bg-white/20" : "bg-black/5 text-black/50"}`}>
                {counts[f.k] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-black/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFCF2] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-4 font-bold">Company</th>
              <th className="text-left p-4 font-bold">Subdomain</th>
              <th className="text-left p-4 font-bold">Plan</th>
              <th className="text-left p-4 font-bold">Amount</th>
              <th className="text-left p-4 font-bold">Status</th>
              <th className="text-left p-4 font-bold">Created</th>
              <th className="text-right p-4 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/8">
            {loading && <tr><td colSpan="7" className="p-7 text-center text-black/50">Loading...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-black/50">No tenants in this view.</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} className="hover:bg-[#FFFCF2]/60">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#F4F8F3] text-[#1B4332] flex items-center justify-center font-bold text-xs">
                      {(t.company_name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-black">{t.company_name}</div>
                      <div className="text-[11px] text-black/50">{t.admin_email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-black/70 font-mono text-xs">{t.subdomain}.brivopos.com</td>
                <td className="p-4 text-black/70">{t.plan_name}</td>
                <td className="p-4 text-black/70 tabular-nums">₦{Number(t.amount || 0).toLocaleString()}</td>
                <td className="p-4"><StatusBadge status={t.status} /></td>
                <td className="p-4 text-black/60 text-xs">{(t.created_at || "").slice(0, 10)}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    {t.status === "pending" && (
                      <button onClick={() => quickStatus(t, "active")} data-testid={`row-approve-${t.subdomain}`}
                        className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D9EEDA]">
                        Approve
                      </button>
                    )}
                    <Link to={`/admin/tenants/${t.id}`} data-testid={`row-view-${t.subdomain}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white border border-black/10 text-black hover:border-black/25">
                      <Eye size={12} /> View
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewTenantDialog open={showNew} onClose={() => setShowNew(false)} plans={plans} onCreated={load} />
    </div>
  );
}
